// src/lib/search.ts
import { DEFAULT_LANGUAGE } from "@/lib/languages";

export type BrightSearchPost = {
  id?: number;
  type?: string;
  slug?: string;
  link?: string;
  post_title?: string;
  title?: { rendered?: string };
  excerpt?: { rendered?: string };
  content?: { rendered?: string } | string;
  post_content?: string;
  acf?: unknown;
};

export interface BrightSearchResponse {
  posts: BrightSearchPost[];
  found_posts: number;
  pagination: {
    total_posts: number;
    posts_per_page: number;
    max_pages: number;
    page: number;
  };
}

function emptyResult(page: number, perPage: number): BrightSearchResponse {
  return {
    posts: [],
    found_posts: 0,
    pagination: {
      total_posts: 0,
      posts_per_page: perPage,
      max_pages: 1,
      page,
    },
  };
}

export async function searchContent(params: {
  s: string;
  lang?: string;
  page?: number;
  perPage?: number;
}): Promise<BrightSearchResponse | null> {
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  if (!API_URL) {
    console.error("[searchContent] NEXT_PUBLIC_API_URL is missing in the browser");
    return null;
  }

  const s = params.s ?? "";
  const page = params.page ?? 1;
  const perPage = params.perPage ?? 12;
  const lang = params.lang ?? DEFAULT_LANGUAGE.polylangCode;

  const qs = new URLSearchParams();
  qs.set("s", s);
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  qs.set("type", "post,page,product");
  if (lang) qs.set("lang", lang);

  const url = `${API_URL}/wp-json/bright/v1/search?${qs.toString()}`;

  try {
    const res = await fetch(url, { cache: "no-store" });
    const text = await res.text();

    if (res.status === 404) {
      if (text.includes("No posts found")) {
        return emptyResult(page, perPage);
      }
      return null;
    }

    if (!res.ok) return null;

    let data: unknown;

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error("[searchContent] JSON parse failed", e);
      return null;
    }

    const parsed = data as Partial<BrightSearchResponse> | null;

    return {
      posts: Array.isArray(parsed?.posts) ? parsed.posts : [],
      found_posts: Number(parsed?.found_posts ?? 0),
      pagination: {
        total_posts: Number(parsed?.pagination?.total_posts ?? 0),
        posts_per_page: Number(parsed?.pagination?.posts_per_page ?? perPage),
        max_pages: Number(parsed?.pagination?.max_pages ?? 1),
        page: Number(parsed?.pagination?.page ?? page),
      },
    };
  } catch (err) {
    console.error("[searchContent] fetch threw", err);
    return null;
  }
}